import * as React from "react";
import { groupBy, map } from "lodash";
import { CircleAlert } from "lucide-react";

import * as DTO from "../../../interfaces/dtos";
import { userFullName, useDebounced, useSocialCoachSession, usePlayerSearch, useSocialNetworks } from "@/utils";
import { PlayerSearchQuery } from "@/services/bedrock";
import { SegmentationDetailServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";

import { AbbreviatedPopover } from "@/components/AbbreviatedPopover";
import { DataTable, Checkbox, Tooltip, Alert, Combobox, Spinner, Input, Badge, Button } from "@/components/ui";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

interface OwnProps {
  selectedPlayers: number[];

  disabled?: boolean;
  showState?: boolean;
  showNetworks?: boolean;
  onChangeSelectedPlayer: (playerIds: number[], checked: boolean) => void;

  /**
   * Search function name
   */
  segmentedPlayers?: number[];
  isInclude?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onPlayersLoaded?: (players: DTO.PlayerWithBranch[]) => void;
  queryKey?: string;
  scrollable?: boolean;
}

export function PaginatedPlayerList({
  selectedPlayers = [],
  showState = false,
  showNetworks = false,
  onChangeSelectedPlayer,
  disabled,
  segmentedPlayers,
  onLoadingChange,
  onPlayersLoaded,
  isInclude = true,
  scrollable = false,
}: OwnProps) {
  const { coachId } = useSocialCoachSession();
  const { postableSocialNetworks: coachSns } = useSocialNetworks(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounced(searchTerm, 300);
  const [viewSelectedOnly, setViewSelectedOnly] = useState(false);

  const [page, setPage] = useState(1);
  const [accumulatedPlayers, setAccumulatedPlayers] = useState<any[]>([]);
  const [isBatchSelecting, setIsBatchSelecting] = useState(false);
  const [shouldSelectAllAfterBatch, setShouldSelectAllAfterBatch] = useState(false);
  const [shouldDeselectAllAfterBatch, setShouldDeselectAllAfterBatch] = useState(false);

  const getSegmentationDetails = React.useCallback((search: string | undefined, page: number) => {
    const types = ["STATE", "REGION", "BRANCH"];
    return SegmentationDetailServices.getSegmentationDetails(types, search, page, 25);
  }, []);
  const segmentationDetails = usePaginatedCombobox(getSegmentationDetails, true);

  const filterBody = useMemo<PlayerSearchQuery>(() => {
    const filter: PlayerSearchQuery = {
      page,
      perPage: 25,
      searchTerm: debouncedSearchTerm,
    };

    if (segmentationDetails.value) {
      const groups = groupBy(segmentationDetails.value, "type");
      const segIds: number[] = [];
      if (groups["BRANCH"]) {
        segIds.push(...map(groups["BRANCH"], "id"));
      }
      if (groups["REGION"]) {
        segIds.push(...map(groups["REGION"], "id"));
      }
      if (groups["STATE"]) {
        segIds.push(...map(groups["STATE"], "id"));
      }
      if (segIds.length > 0) {
        filter.segmentationIds = segIds;
      }
    }

    if (viewSelectedOnly) {
      filter.playerIds = selectedPlayers.length > 0 ? selectedPlayers : [-1];
    } else if (segmentedPlayers && segmentedPlayers.length > 0 && isInclude) {
      filter.playerIds = segmentedPlayers;
    }

    return filter;
  }, [
    segmentationDetails.value,
    segmentedPlayers,
    isInclude,
    viewSelectedOnly,
    selectedPlayers,
    debouncedSearchTerm,
    page,
  ]);

  const { searchPlayers } = usePlayerSearch();
  const { players, isFetching } = searchPlayers(filterBody);
  const hasNextPage = page < (players?.pagination?.totalPages || 1);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, viewSelectedOnly, segmentationDetails.value, segmentedPlayers, isInclude]);

  useEffect(() => {
    if (!isFetching && players?.content) {
      if (players.pagination?.page === page) {
        if (page === 1) {
          setAccumulatedPlayers(players.content);
        } else {
          setAccumulatedPlayers(prev => {
            const newPlayers = players.content.filter((p: any) => !prev.some((existing: any) => existing.id === p.id));
            return [...prev, ...newPlayers];
          });
        }
      }
    }
  }, [players, page, isFetching]);

  const mappedPlayers = useMemo<DTO.PlayerWithBranch[]>(() => {
    let result = accumulatedPlayers;
    if (segmentedPlayers && segmentedPlayers.length > 0 && !isInclude) {
      result = result.filter(player => !segmentedPlayers.includes(player.id));
    }

    return result.map(player => {
      const branchNames =
        player.segmentationDetails?.filter((s: any) => s.type === "BRANCH")?.map((s: any) => s.name) || [];
      const regionNames =
        player.segmentationDetails?.filter((s: any) => s.type === "REGION")?.map((s: any) => s.name) || [];
      const stateCodes =
        player.segmentationDetails?.filter((s: any) => s.type === "STATE")?.map((s: any) => s.name) || [];

      return {
        photoUrl: player.photoUrl,
        email: player.email || "",
        branchNames,
        regionNames,
        stateCodes,
        info: {
          playerId: player.id,
          id: player.id,
          userId: (player as any).userId || (player as any).playerAccount?.userId || player.id,
          guid: player.guid,
          firstName: player.firstName || "",
          lastName: player.lastName || "",
          email: player.email,
          phone: player.phone,
          photoUrl: player.photoUrl,
          socialNetworks:
            player.activeSocialNetworkProfiles?.map((sn: any) => ({
              username: sn.socialIdentity?.username || "",
              networkId: sn.socialNetworkId,
            })) || [],
        },
      } as DTO.PlayerWithBranch;
    });
  }, [accumulatedPlayers, segmentedPlayers, isInclude]);

  useEffect(() => {
    if (isBatchSelecting && !isFetching && players) {
      const totalPages = players.pagination?.totalPages || 1;
      if (page === totalPages) {
        setIsBatchSelecting(false);
      } else {
        setPage(prev => prev + 1);
      }
    }
  }, [isBatchSelecting, page, players, isFetching]);

  useEffect(() => {
    if (!isBatchSelecting && shouldSelectAllAfterBatch && mappedPlayers.length > 0) {
      setShouldSelectAllAfterBatch(false);
      const allPlayersIds = mappedPlayers.map(p => p.info!.playerId!);
      onChangeSelectedPlayer(allPlayersIds, true);
    }
  }, [isBatchSelecting, shouldSelectAllAfterBatch, mappedPlayers, onChangeSelectedPlayer]);

  useEffect(() => {
    if (!isBatchSelecting && shouldDeselectAllAfterBatch && mappedPlayers.length > 0) {
      setShouldDeselectAllAfterBatch(false);
      const allPlayersIds = mappedPlayers.map(p => p.info!.playerId!);
      onChangeSelectedPlayer(allPlayersIds, false);
    }
  }, [isBatchSelecting, shouldDeselectAllAfterBatch, mappedPlayers, onChangeSelectedPlayer]);

  useEffect(() => {
    onPlayersLoaded && onPlayersLoaded(mappedPlayers);
  }, [mappedPlayers, onPlayersLoaded]);

  useEffect(() => {
    onLoadingChange && onLoadingChange(isFetching);
  }, [isFetching, onLoadingChange]);

  const allPlayersSelected =
    mappedPlayers.length > 0 && mappedPlayers.every(p => selectedPlayers.includes(p.info!.playerId!));

  const columns = React.useMemo<ColumnDef<DTO.PlayerWithBranch>[]>(() => {
    const cols: ColumnDef<DTO.PlayerWithBranch>[] = [
      {
        id: "select",
        header: () => (
          <Checkbox
            disabled={isBatchSelecting}
            checked={allPlayersSelected}
            className="bg-white"
            onCheckedChange={checked => {
              if (checked && hasNextPage) {
                setIsBatchSelecting(true);
                setShouldSelectAllAfterBatch(true);
              } else if (!checked && hasNextPage) {
                setIsBatchSelecting(true);
                setShouldDeselectAllAfterBatch(true);
              } else {
                const allPlayersIds = mappedPlayers.map(p => p.info!.playerId!);
                onChangeSelectedPlayer(allPlayersIds, checked);
              }
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedPlayers.includes(row.original.info!.playerId!)}
            onCheckedChange={checked => {
              onChangeSelectedPlayer([row.original.info!.playerId!], checked);
            }}
          />
        ),
        enableSorting: false,
        meta: { intrinsic: true },
      },
      {
        accessorKey: "info.firstName",
        header: "Name",
        cell: ({ row }) => {
          const { info } = row.original;
          return (
            <div className="flex items-center gap-2">
              <AbbreviatedPopover text={userFullName(info)} maxLength={50} />
              {!info.socialNetworks?.length && (
                <Tooltip>
                  <Tooltip.Trigger render={<CircleAlert className="size-4 text-orange-500" />} />
                  <Tooltip.Content>No social networks connected.</Tooltip.Content>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => <AbbreviatedPopover text={row.original.email} maxLength={50} />,
      },
    ];

    if (showNetworks) {
      cols.push({
        id: "socials",
        header: "Socials",
        cell: ({ row }) => {
          const actives = row.original.info?.socialNetworks?.map(s => s.networkId) || [];
          return <SocialNetworksSection actives={actives} allSocialNetworks={coachSns} wrap={false} />;
        },
      });
    }

    return cols;
  }, [
    mappedPlayers,
    selectedPlayers,
    onChangeSelectedPlayer,
    showState,
    showNetworks,
    coachSns,
    allPlayersSelected,
    hasNextPage,
    isBatchSelecting,
    shouldSelectAllAfterBatch,
    shouldDeselectAllAfterBatch,
  ]);

  const infiniteScrollProps = useMemo(() => {
    return {
      hasNextPage,
      isFetchingNextPage: isFetching,
      fetchNextPage: () => {
        if (hasNextPage && !isFetching) {
          setPage(prev => prev + 1);
        }
      },
    };
  }, [hasNextPage, isFetching]);

  return (
    <div className={cn("flex flex-col gap-2", scrollable && "flex-1 min-h-0 h-full")}>
      <div className="flex flex-row gap-4 items-center flex-wrap">
        <Input
          placeholder="Search name or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="max-w-xs bg-white"
        />
        <Combobox
          {...segmentationDetails}
          itemToStringLabel={segmentationDetail => segmentationDetail.name || "-"}
          itemToBadge={segmentationDetail => segmentationDetail.type}
          placeholder="Filter by Segmentation"
          className="min-w-100 bg-white"
        />

        {isFetching && <Spinner />}

        <div className="flex-1" />

        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewSelectedOnly(prev => !prev)}
                className={cn(
                  "group h-9 px-3.5 gap-2 rounded-full font-medium transition-all shadow-sm border-slate-200 bg-white hover:border-slate-300",
                  viewSelectedOnly
                    ? "border-primary/50 bg-blue-50/70 hover:bg-blue-50 text-primary"
                    : "hover:bg-slate-50 text-slate-600"
                )}
              >
                <div
                  className={cn(
                    "size-2 rounded-full shrink-0 transition-all",
                    viewSelectedOnly ? "bg-primary animate-pulse" : "bg-slate-300 group-hover:bg-primary"
                  )}
                />
                <span className="text-xs">Selected:</span>
                <span className="font-extrabold text-xs">{selectedPlayers.length}</span>

                {viewSelectedOnly ? (
                  <span className="text-[10px] font-semibold bg-blue-100 text-primary px-1.5 py-0.5 rounded-full uppercase tracking-wider ml-0.5 transition-all">
                    Filter On
                  </span>
                ) : (
                  <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full uppercase tracking-wider ml-0.5 transition-all">
                    Click to Filter
                  </span>
                )}
              </Button>
            }
          />
          <Tooltip.Content side="top" sideOffset={6}>
            {viewSelectedOnly
              ? "Click to show all available players"
              : "Click to isolate and view selected players only"}
          </Tooltip.Content>
        </Tooltip>
      </div>

      {isBatchSelecting && (
        <Alert
          variant="default"
          className="bg-amber-50 border-amber-200 text-amber-800 flex flex-row items-center gap-2 py-2 px-3 mt-1 shadow-sm animate-in slide-in-from-top-1 duration-200"
        >
          <Spinner className="size-3.5 text-amber-600 animate-spin" />
          <span className="text-xs font-semibold">
            {shouldSelectAllAfterBatch ? "Selecting" : "Deselecting"} all players... Loading page {page} of{" "}
            {players?.pagination?.totalPages || "?"}
          </span>
        </Alert>
      )}

      <div className={cn("bg-white", scrollable && "flex flex-col flex-1 min-h-0 h-full")}>
        {isFetching || mappedPlayers.length > 0 ? (
          <DataTable
            columns={columns}
            data={mappedPlayers}
            rowCount={mappedPlayers.length}
            loading={isFetching && mappedPlayers.length === 0}
            disabled={disabled}
            scrollable={scrollable}
            infiniteScroll={infiniteScrollProps}
          />
        ) : (
          <Alert variant="default" className="mt-4">
            <Alert.Title>No results</Alert.Title>
            <Alert.Description>There are no players matching your search criteria.</Alert.Description>
          </Alert>
        )}
      </div>
    </div>
  );
}
